// ═══ 사진 전체화면 뷰어 (세로 스크롤 연속) ═══
// #288 — 검차 사진이 50~60장이라 한 장씩 넘기는 페이징이 비효율 → 모바일 앱처럼
// 세로로 쭉 스크롤하며 훑는 방식으로 교체. 핀치줌/스와이프/좌우 넘기기는 제거.
// photos 배열 + viewIndex/setViewIndex만 받는 컴포넌트 API는 유지(호출부 무수정).
import React, { useEffect, useRef } from 'react';
import { API, API_ORIGIN } from '../utils/api';
import { parseEvalPhotoUrl } from '../utils/evalPhotoUrl';

var VIDEO_RE = /\.(mp4|mov|avi|webm|3gp)$/i;
var TOP_BAR_H = 56;

// #288 — 원본은 서버 스트림 라우트를 <img>/<video> src 로 직접 사용.
// p.url 직접 참조는 절대 URL(test 도메인) 저장분과 정적 마운트 부재(운영) 양쪽에서 깨짐.
// fetch/blob 방식은 불가 — cross-origin 리다이렉트를 따라갈 때 브라우저가 Origin 을 null 로
// 직렬화해 버킷 allowlist CORS 에 걸린다(Failed to fetch). <img>/<video> 는 no-cors 라 무관.
// /view = /download 별칭 — '*/download' 를 차단하는 광고차단 확장 회피 (#288)
function fullSrcOf(url) {
  if (!url) return '';
  var pp = parseEvalPhotoUrl(url);
  if (!pp) return url; // blob: 등 비표준 — 그대로 사용
  return API + '/eval/orders/' + pp.orderId + '/photos/' + encodeURIComponent(pp.filename) + '/view';
}

function PhotoLightbox(props) {
  var photos = props.photos || [];
  var viewIndex = props.viewIndex;
  var setViewIndex = props.setViewIndex;
  var isOpen = viewIndex !== null && viewIndex !== undefined && !!photos[viewIndex];

  var itemRefs = useRef([]);

  // 클릭한 사진에서 시작 — 열릴 때 / viewIndex 변경 시 해당 항목으로 즉시 점프.
  // 각 항목 래퍼가 minHeight 로 자리를 잡고 있어 로드 전에도 위치 계산이 안정적.
  useEffect(function() {
    if (!isOpen) return;
    var el = itemRefs.current[viewIndex];
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'start', behavior: 'auto' });
  }, [isOpen, viewIndex]);

  // Escape 닫기
  useEffect(function() {
    if (!isOpen) return;
    var onKey = function(e) { if (e.key === 'Escape') setViewIndex(null); };
    window.addEventListener('keydown', onKey);
    return function() { window.removeEventListener('keydown', onKey); };
  }, [isOpen, setViewIndex]);

  if (!isOpen) return null;

  var close = function() { setViewIndex(null); };
  var download = function(e, url) {
    e.stopPropagation();
    var pp = parseEvalPhotoUrl(url);
    if (pp) window.open(API_ORIGIN + '/api/eval/orders/' + pp.orderId + '/photos/' + encodeURIComponent(pp.filename) + '/download', '_blank');
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.95)', zIndex: 9999,
      overflowY: 'auto', overflowX: 'hidden',
      overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch',
    }}>
      {/* 상단 고정 바 — 스크롤과 무관하게 고정. 바 영역 스크롤은 통과시키고 버튼만 클릭 수신 */}
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, height: TOP_BAR_H, zIndex: 10,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 16px', pointerEvents: 'none',
        background: 'linear-gradient(rgba(0,0,0,0.85), rgba(0,0,0,0))',
      }}>
        <div style={{ color: '#fff', fontSize: 14, fontWeight: 600, fontFamily: "'Pretendard',sans-serif" }}>
          사진 {photos.length}장
        </div>
        <button onClick={close} style={{
          pointerEvents: 'auto', background: 'rgba(255,255,255,0.2)', border: 'none',
          color: '#fff', fontSize: 24, width: 40, height: 40, borderRadius: '50%', cursor: 'pointer',
        }}>×</button>
      </div>

      {/* 사진 스택 — 원본 비율 유지 + lazy 로드(60장 대응) */}
      <div style={{ padding: (TOP_BAR_H + 12) + 'px 0 48px' }}>
        {photos.map(function(p, i) {
          var url = p.url || '';
          var src = fullSrcOf(url);
          var isVideo = VIDEO_RE.test(p.filename || url);
          return (
            <div
              key={i}
              ref={function(el) { itemRefs.current[i] = el; }}
              style={{
                width: '100%', maxWidth: 'min(92vw, 900px)', margin: '0 auto 24px',
                scrollMarginTop: TOP_BAR_H,
              }}
            >
              <div style={{
                minHeight: '40vh', background: '#111', borderRadius: '4px 4px 0 0',
                overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {isVideo
                  ? <video src={src} controls preload="metadata" style={{ width: '100%', height: 'auto', display: 'block' }} />
                  : <img src={src} alt={p.memo || ''} loading="lazy" decoding="async"
                      style={{ width: '100%', height: 'auto', display: 'block' }} />
                }
              </div>
              {/* 캡션 바 — 번호 / 메모 / 저장 */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                background: 'rgba(255,255,255,0.1)', borderRadius: '0 0 4px 4px',
              }}>
                <span style={{ color: '#94a3b8', fontSize: 12, fontFamily: "'Pretendard',sans-serif", flexShrink: 0 }}>
                  {i + 1} / {photos.length}
                </span>
                <span style={{ color: '#fff', fontSize: 13, flex: 1, fontFamily: "'Pretendard',sans-serif", wordBreak: 'break-all' }}>
                  {p.memo || ''}
                </span>
                {url && !url.startsWith('blob:') && (
                  <button
                    onClick={function(e) { download(e, url); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 4, color: '#fff', fontSize: 12,
                      background: 'rgba(255,255,255,0.2)', padding: '6px 14px', borderRadius: 6,
                      border: 'none', cursor: 'pointer', fontWeight: 600, flexShrink: 0,
                      fontFamily: "'Pretendard',sans-serif",
                    }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    저장
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default PhotoLightbox;
